import React, { useId } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { Button } from "../../../components/forms/Buttons/Button";
import { cardClass, linkButtonClass } from "../../Exams/TestBuilder/ui";
import { LatestTag, VersionByline, VersionStatus, versionLabel } from "./parts";

function VersionRow({ version, latest, open, ticked, tickable, onTick, to, onOpen }) {
  const { t } = useTranslation();
  return (
    <li className={`relative flex items-start gap-3 px-4 py-3 transition duration-200 sm:px-5 ${open ? "bg-blue-50/70" : "hover:bg-slate-50"}`}>
      {open && <span className="absolute inset-y-0 left-0 w-1 bg-blue-600" aria-hidden="true" />}
      {tickable && (
        // Above the row's link, which covers the rest of the row; a bigger target than the box itself.
        <label className="relative z-10 -m-2 flex shrink-0 cursor-pointer items-center p-2">
          <input
            type="checkbox"
            checked={ticked}
            onChange={() => onTick(version.number)}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-600"
          />
          <span className="sr-only">{t("question_versions.select_label", { number: version.number })}</span>
        </label>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={to}
            replace
            onClick={onOpen}
            aria-current={open ? "true" : undefined}
            className="text-sm font-semibold text-slate-900 outline-none after:absolute after:inset-0 focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-blue-600"
          >
            {versionLabel(t, version.number)}
          </Link>
          {latest && <LatestTag />}
          <span className="ml-auto">
            <VersionStatus code={version.status_code} />
          </span>
        </div>
        <VersionByline version={version} className="mt-1" />
      </div>
    </li>
  );
}

/**
 * The versions, newest first: each opens (a link, `linkTo(number)`), and two ticked ones are compared. `open` is
 * the number of the version shown beside the list, `ticked` the numbers ticked for a comparison.
 */
export default function VersionList({
  versions,
  open,
  ticked,
  onTick,
  linkTo,
  onOpen,
  onCompare,
  headingRef,
  className = "",
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const hintId = useId();
  const latest = versions[0]?.number;
  const comparable = versions.length > 1;
  const [first, second] = [...ticked].sort((a, b) => a - b);
  const hint =
    ticked.length === 2
      ? t("question_versions.select_ready", { from: first, to: second })
      : ticked.length === 1
      ? t("question_versions.select_one_more")
      : t("question_versions.select_hint");

  return (
    <section aria-labelledby={titleId} className={`${cardClass} ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 sm:px-5">
        <h3
          id={titleId}
          ref={headingRef}
          tabIndex={-1}
          className="flex items-center gap-2 text-base font-semibold tracking-tight text-slate-900 focus:outline-none"
        >
          {t("question_versions.title")}
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium tabular-nums text-slate-600">
            {versions.length}
          </span>
        </h3>
        {comparable && (
          <button type="button" onClick={() => onCompare(versions[1].number, latest)} className={`${linkButtonClass} max-md:min-h-[2.75rem]`}>
            <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t("question_versions.latest_changes")}
          </button>
        )}
      </div>

      <ol className="divide-y divide-slate-100">
        {versions.map((version) => (
          <VersionRow
            key={version.number}
            version={version}
            latest={version.number === latest}
            open={version.number === open}
            ticked={ticked.includes(version.number)}
            tickable={comparable}
            onTick={onTick}
            to={linkTo(version.number)}
            onOpen={onOpen}
          />
        ))}
      </ol>

      {comparable ? (
        // Stays in view at the bottom of the screen while the list is longer than it, above the rows' tick boxes
        // (z-10) that scroll under it.
        <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-b-2xl border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
          <p id={hintId} role="status" className="min-w-0 text-xs text-slate-500">
            {hint}
          </p>
          <Button
            text={t("question_versions.compare")}
            Icon={ArrowsRightLeftIcon}
            color="primary"
            disabled={ticked.length !== 2}
            aria-describedby={hintId}
            onClick={() => onCompare(first, second)}
            className="max-md:h-11"
          />
        </div>
      ) : (
        <p className="rounded-b-2xl border-t border-slate-100 px-4 py-3 text-xs text-slate-500 sm:px-5">
          {t("question_versions.single_hint")}
        </p>
      )}
    </section>
  );
}
